import { supabase } from '../../../shared/lib/supabaseClient';
import { logActivity } from '../../../shared/services/activityLogService';

// A subject's roster isn't stored: it's the students in the subject's grade level
// whose section is one of the sections assigned to the subject. Students still waiting for super
// admin approval are left off: they can't be graded yet (check_student_approved in schema.sql).
export async function getSubjectRoster(gradeLevel, sections) {
  if (!gradeLevel || sections.length === 0) return [];

  const { data, error } = await supabase
    .from('students')
    .select('id, lrn, firstname, lastname, middlename, gender, section, track')
    .eq('grade_level', gradeLevel)
    .in('section', sections)
    .eq('approved', true)
    .order('lastname', { ascending: true })
    .order('firstname', { ascending: true });
  if (error) throw error;
  return data;
}

export async function getAssessments(subjectId, component) {
  const { data, error } = await supabase
    .from('assessments')
    .select('*, assessment_scores(student_id, score, comment)')
    .eq('subject_id', subjectId)
    .eq('component', component)
    .order('term', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

// Writes the scores as the assessment's full set: blank scores are removed.
async function replaceScores(assessmentId, scores) {
  if (scores.length > 0) {
    const rows = scores.map(({ student_id, score, comment }) => ({
      assessment_id: assessmentId,
      student_id,
      score,
      comment: comment || null,
    }));
    const { error } = await supabase
      .from('assessment_scores')
      .upsert(rows, { onConflict: 'assessment_id,student_id' });
    if (error) throw error;
  }

  let cleanup = supabase.from('assessment_scores').delete().eq('assessment_id', assessmentId);
  if (scores.length > 0) {
    cleanup = cleanup.not('student_id', 'in', `(${scores.map((s) => s.student_id).join(',')})`);
  }
  const { error } = await cleanup;
  if (error) throw error;
}

// `scores` is [{ student_id, score, comment }] for the students that have a score; `comment` is optional.
export async function saveAssessment({ id, subjectId, component, term, section, title, maxScore, assessmentDate, scores }) {
  const fields = {
    subject_id: subjectId,
    component,
    term,
    section: section || null,
    title,
    max_score: maxScore,
    assessment_date: assessmentDate || null,
  };

  let assessmentId = id;
  if (id) {
    const { error } = await supabase.from('assessments').update(fields).eq('id', id);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from('assessments').insert(fields).select('id').single();
    if (error) throw error;
    assessmentId = data.id;
  }

  try {
    await replaceScores(assessmentId, scores);
  } catch (err) {
    // Don't leave a new assessment behind without the scores that were entered for it.
    if (!id) await supabase.from('assessments').delete().eq('id', assessmentId);
    throw err;
  }

  await logActivity(id ? 'assessment_updated' : 'assessment_created', `${id ? 'Updated' : 'Added'} ${title}${section ? ` (${section})` : ''}`);
}

export async function deleteAssessment(id, title) {
  const { error } = await supabase.from('assessments').delete().eq('id', id);
  if (error) throw error;
  await logActivity('assessment_deleted', `Removed ${title}`);
}

// Every assessment one section takes in a term, oldest first, with its scores. Assessments saved
// without a section count for every section.
export async function getSectionTermAssessments(subjectId, term, section) {
  const { data, error } = await supabase
    .from('assessments')
    .select('id, component, title, max_score, section, assessment_scores(student_id, score)')
    .eq('subject_id', subjectId)
    .eq('term', term)
    .or(`section.is.null,section.eq."${section.replace(/"/g, '\\"')}"`)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data;
}

// The school's transmutation table (see transmute_grade in schema.sql), lowest band first.
export async function getTransmutationTable() {
  const { data, error } = await supabase
    .from('transmutation_table')
    .select('lower_bound, transmuted')
    .order('lower_bound', { ascending: true });
  if (error) throw error;
  return data;
}

// Computed by the subject_grades view; a student has a row once they have any score in the term.
export async function getSubjectGrades(subjectId, term) {
  const { data, error } = await supabase
    .from('subject_grades')
    .select(
      'student_id, written_work_pct, performance_task_pct, examination_pct, attendance_pct, initial_grade, term_grade'
    )
    .eq('subject_id', subjectId)
    .eq('term', term);
  if (error) throw error;
  return data;
}

export async function getAttendanceForDate(subjectId, date) {
  const { data, error } = await supabase
    .from('attendance_records')
    .select('student_id, status, term')
    .eq('subject_id', subjectId)
    .eq('attendance_date', date);
  if (error) throw error;
  return data;
}

// `records` is [{ student_id, status }]; saving a date again overwrites it.
export async function saveAttendance(subjectId, date, term, records) {
  const rows = records.map(({ student_id, status }) => ({
    subject_id: subjectId,
    student_id,
    attendance_date: date,
    term,
    status,
  }));
  const { error } = await supabase
    .from('attendance_records')
    .upsert(rows, { onConflict: 'subject_id,student_id,attendance_date' });
  if (error) throw error;
  await logActivity('attendance_saved', `Saved attendance for ${date} (Term ${term})`);
}

// Removes the date's saved records for the given students, leaving them unmarked.
export async function resetAttendance(subjectId, date, studentIds) {
  if (studentIds.length === 0) return;
  const { error } = await supabase
    .from('attendance_records')
    .delete()
    .eq('subject_id', subjectId)
    .eq('attendance_date', date)
    .in('student_id', studentIds);
  if (error) throw error;
  await logActivity('attendance_reset', `Reset attendance for ${date}`);
}
